import { FiEdit } from 'react-icons/fi';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { ViewIcon } from '@chakra-ui/icons';
import {
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { DeleteOutlined } from '@ant-design/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import CaseClusterTag from '@/components/CaseClusterTag/CaseClusterTag';
import ClientTag from '@/components/ClientTag/ClientTag';
import { CaseCluster, Client, Meeting } from '@/services/API';
import { useConfirmModal } from '@/ui/ConfirmModal/ConfirmModal';
import {
  createArrayFilterFn,
  stringArrayFilterFn,
} from '@/ui/TanstackTable/Filter/array/arrayFilterFns';
import dateInRangeFilterFn from '@/ui/TanstackTable/Filter/date/dateInRangeFilterFn';
import { parseDate } from '@/utils/helpers/parseDate';

export type AdminCaseClusterListItemType = CaseCluster & {
  linkedMeetings: Meeting[];
  canBeDeleted: boolean;
  clients: Client[];
  clientText: string;
};

const columnHelper = createColumnHelper<AdminCaseClusterListItemType>();

type Args = {
  t: TFunction<'translation', undefined, 'translation'>;
  onEditClick: (data: CaseCluster) => void;
  onDeleteClick: (caseClusterID: string) => void;
  confirmModalUtils: ReturnType<typeof useConfirmModal>;
};

const getColumns = ({ t, onDeleteClick, onEditClick, confirmModalUtils }: Args) => {
  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (props) => (
        <Tooltip label={props.getValue()}>
          <Text>{props.getValue()}</Text>
        </Tooltip>
      ),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
      filterFn: dateInRangeFilterFn,
      cell: (props) => <Text>{parseDate(props.getValue() || '')}</Text>,
    }),
    columnHelper.accessor('cases', {
      header: 'Cases Included',
      filterFn: stringArrayFilterFn,
      cell: (props) => {
        const cases = props.getValue();
        return (
          <Flex flexDir="row" gap={2} flexWrap="wrap">
            {cases.map((elem) => (
              <CaseClusterTag key={elem} name={elem} />
            ))}
          </Flex>
        );
      },
    }),
    columnHelper.accessor('linkedMeetings', {
      header: 'Linked Meetings',
      filterFn: createArrayFilterFn<Meeting>('name'),
      cell: (props) => {
        const elems = props.getValue();
        const { isOpen, onOpen, onClose } = useDisclosure();
        const firstElem = elems[0];

        return (
          <>
            <Flex flexDir="row" gap={2} align="center">
              {firstElem && (
                <Tooltip
                  label={
                    <div>
                      {firstElem.eventDate && <h2>{parseDate(firstElem.eventDate)}</h2>}
                      {firstElem.name}
                    </div>
                  }
                >
                  <Tag colorScheme="blue" w="min">
                    <Text textOverflow="ellipsis" noOfLines={1}>
                      {firstElem.name}
                    </Text>
                  </Tag>
                </Tooltip>
              )}

              <Tooltip label="View all linked meetings">
                {elems.length > 1 && (
                  <IconButton
                    icon={<ViewIcon />}
                    aria-label="View all linked meetings"
                    size="sm"
                    onClick={onOpen}
                  />
                )}
              </Tooltip>
            </Flex>

            <Modal isOpen={isOpen} onClose={onClose} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Linked Meetings</ModalHeader>
                <ModalCloseButton />
                <ModalBody maxH="80vh" overflowY="auto" p={4}>
                  <Flex flexDir="column" gap={3} mb={4} justifyContent="center" alignItems="center">
                    {elems.map((elem) => (
                      <Tag key={elem.id} colorScheme="blue" w="fit-content">
                        <Tooltip
                          label={
                            <div>
                              {elem.eventDate && <h2>{parseDate(elem.eventDate)}</h2>}
                              {elem.name}
                            </div>
                          }
                        >
                          <Text textOverflow="ellipsis" noOfLines={1}>
                            {elem.name}
                          </Text>
                        </Tooltip>
                      </Tag>
                    ))}
                  </Flex>
                </ModalBody>
              </ModalContent>
            </Modal>
          </>
        );
      },
    }),
    columnHelper.accessor('clientText', {
      id: 'clients',
      header: 'Client',
      // filterFn: createArrayFilterFn<Client>('clientName'),
      cell: (props) => {
        const clients = props.row.original.clients;
        return (
          <Flex flexDir="row" gap={2} flexWrap="wrap">
            {clients.map((elem) => (
              <ClientTag key={elem.id} client={elem.clientName} />
            ))}
          </Flex>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 85,
      cell: (props) => {
        const data = props.row.original;
        return (
          <>
            <Menu closeOnSelect={false}>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<HiOutlineDotsVertical />}
                variant="outline"
                size="xs"
                border="none"
              />
              <MenuList>
                <MenuItem
                  icon={<FiEdit />}
                  onClick={() => {
                    onEditClick(data);
                  }}
                >
                  {t('common.edit')}
                </MenuItem>
                <MenuItem
                  icon={<DeleteOutlined />}
                  isDisabled={!data.canBeDeleted}
                  onClick={() => {
                    confirmModalUtils.confirm({
                      title: (
                        <div>
                          {t('admin.caseClusters.delete.confirmText')}
                          <br />
                          {t('meeting.header.confirmRestart')}
                        </div>
                      ),
                      onConfirm: () => onDeleteClick(data.id),
                    });
                  }}
                >
                  {t('common.delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          </>
        );
      },
    }),
  ];

  return columns;
};

export default getColumns;
